import React, { useState, useContext, useEffect } from 'react';
import {
  Flex,
  Text,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  useToast
} from "@chakra-ui/react";
import axios from 'axios';
import { AuthContext } from '../../../contexts/AuthContext';
import { FaPlus } from 'react-icons/fa';
import { useToastError } from '../../../utils/toastError';

const BonusCriteriaCard = ({ title, minValue, bonusValue, onChangeMin, onChangeBonus, onRemove }) => {
  return (
    <Flex
      width="100%"
      gap="20px"
      padding="10px 20px"
      borderRadius="20px"
      backgroundColor="#1F2630"
      marginBottom="10px"
    >
      <Flex flexDirection="column" flex={1}>
        <Text color="white" fontSize="18px" mb={2}>
          {title}
        </Text>
        <Flex gap="20px">
          <Flex flexDirection="column" flex={1}>
            <Text color="white" fontSize="14px" mb={1}>
              Apartir de (R$)
            </Text>
            <input
              type="number"
              value={minValue}
              onChange={onChangeMin}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: '1px solid white',
                color: 'white',
                fontSize: '22px',
                fontWeight: '700',
                width: '100%',
                outline: 'none'
              }}
            />
          </Flex>
          <Flex flexDirection="column" flex={1}>
            <Text color="white" fontSize="14px" mb={1}>
              Bônus (R$)
            </Text>
            <input
              type="number"
              value={bonusValue}
              onChange={onChangeBonus}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: '1px solid white',
                color: 'white',
                fontSize: '22px',
                fontWeight: '700',
                width: '100%',
                outline: 'none'
              }}
            />
          </Flex>
        </Flex>
      </Flex>
      <Flex alignItems="center" justifyContent="flex-end" gap="10px">
        {onRemove && (
          <Button
            size="sm"
            colorScheme="red"
            onClick={onRemove}
          >
            Remover
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

const BonusPaymentModule = ({ isOpen, onClose, moduleId, machineName }) => {
  const [bonusCriteria, setBonusCriteria] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [, setConfigurations] = useState([]);
  const { authInfo } = useContext(AuthContext);
  const { dataUser } = authInfo;
  const toastError = useToastError();
  const toast = useToast();
  const [isActvateBonus, setIsActvateBonus] = useState(false);

  const fetchConfigurations = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        dataUser.key === "ADMIN"
          ? `${process.env.REACT_APP_SERVIDOR}/configuracoes-modulo-adm/${moduleId}`
          : `${process.env.REACT_APP_SERVIDOR}/configuracoes-modulo/${moduleId}`,
        {
          headers: {
            "x-access-token": authInfo?.dataUser?.token,
            "content-type": "application/json",
          }
        }
      );

      if (response.data) {
        setConfigurations(response.data);
        
        const bonusAtivo = response.data.find(config => config.chave === 'bonus_ativo');
        const bonusHabilitado = bonusAtivo && (bonusAtivo.valor.toLowerCase() === 'true');
        setIsActvateBonus(bonusHabilitado);

        const bonusCriterias = response.data
          .filter(config => config.chave.startsWith('bonus_criterio_'))
          .map(config => {
            const [minValue, bonusValue] = config.valor.split('|');
            const id = parseInt(config.chave.replace('bonus_criterio_', ''));
            return {
              id,
              minValue: parseFloat(minValue || 0),
              bonusValue: parseFloat(bonusValue || 0)
            };
          })
          .sort((a, b) => a.id - b.id);

        if (bonusCriterias.length === 0) {
          setBonusCriteria([{ id: 1, minValue: 0, bonusValue: 0 }]);
        } else {
          setBonusCriteria(bonusCriterias);
        }
      }
    } catch (error) {
      toastError.error("Erro ao carregar configurações do módulo");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && moduleId) {
      fetchConfigurations();
    }
  }, [isOpen, moduleId]);

  const handleAddCriteria = () => {
    const newId = bonusCriteria.length > 0
      ? Math.max(...bonusCriteria.map(c => c.id)) + 1
      : 1;
    setBonusCriteria([...bonusCriteria, {
      id: newId,
      minValue: 0,
      bonusValue: 0
    }]);
  };

  const handleMinValueChange = (id, value) => {
    setBonusCriteria(bonusCriteria.map(criteria =>
      criteria.id === id ? { ...criteria, minValue: value } : criteria
    ));
  };

  const handleBonusValueChange = (id, value) => {
    setBonusCriteria(bonusCriteria.map(criteria =>
      criteria.id === id ? { ...criteria, bonusValue: value } : criteria
    ));
  };

  const handleDeleteCriteria = async (id) => {
    setIsLoading(true);
    try {
      const configuracoes = [{
        [`bonus_criterio_${id}`]: ''
      }];

      await axios.delete(
        dataUser.key === "ADMIN"
          ? `${process.env.REACT_APP_SERVIDOR}/configuracao-modulo-adm/${moduleId}`
          : `${process.env.REACT_APP_SERVIDOR}/configuracao-modulo/${moduleId}`,
        {
          headers: {
            "x-access-token": authInfo?.dataUser?.token,
            "content-type": "application/json",
          },
          data: { configuracoes }
        }
      );

      // Remove o critério do estado local
      setBonusCriteria(bonusCriteria.filter(criteria => criteria.id !== id));

      toastError.success('Critério removido com sucesso');
    } catch (error) {
      toastError.error("Erro ao remover critério de bônus");
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleBonusCriteria = async () => {
    setIsLoading(true);
    try {
      // Toggle the current state and convert to string
      const newBonusActiveState = !isActvateBonus;

      // Prepare the configuration object as expected by the backend
      const configuracoes = [
        {
          bonus_ativo: newBonusActiveState.toString()
        }
      ];

      // Make the API request to update the configuration
      await axios.put(
        dataUser.key === "ADMIN"
          ? `${process.env.REACT_APP_SERVIDOR}/configuracao-modulo-adm/${moduleId}`
          : `${process.env.REACT_APP_SERVIDOR}/configuracao-modulo/${moduleId}`,
        { configuracoes },
        {
          headers: {
            "x-access-token": authInfo?.dataUser?.token,
            "content-type": "application/json",
          }
        }
      );

      // Update the local state
      setIsActvateBonus(newBonusActiveState);

      toast({
        title: newBonusActiveState
          ? "Critérios de bônus ativados!"
          : "Critérios de bônus desativados!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right"
      });
    } catch (error) {
      toastError.error(
        isActvateBonus
          ? "Erro ao desativar critérios de bônus"
          : "Erro ao ativar critérios de bônus"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveBonusCriteria = async () => {
    setIsLoading(true);
    try {
      const configuracoes = bonusCriteria.map(criteria => ({
        [`bonus_criterio_${criteria.id}`]: `${criteria.minValue}|${criteria.bonusValue}`
      }));

      await axios.put(
        dataUser.key === "ADMIN"
          ? `${process.env.REACT_APP_SERVIDOR}/configuracao-modulo-adm/${moduleId}`
          : `${process.env.REACT_APP_SERVIDOR}/configuracao-modulo/${moduleId}`,
        { configuracoes },
        {
          headers: {
            "x-access-token": authInfo?.dataUser?.token,
            "content-type": "application/json",
          }
        }
      );

      toastError.success('Critérios salvos com sucesso');
    } catch (error) {
      toastError.error("Erro ao salvar critérios de bônus");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent backgroundColor="#1a202c">
        <Flex position="absolute" top="10px" right="40px">
          <Button
            size="sm"
            colorScheme={isActvateBonus ? "red" : "green"}
            onClick={handleToggleBonusCriteria}
            isLoading={isLoading}
            mr={2}
          >
            {isActvateBonus ? "Desativar Critérios" : "Ativar Critérios"}
          </Button>
        </Flex>
        <ModalCloseButton color="white" />
        <ModalBody padding="20px">
            <Text color="white" fontSize="24px">
              O bônus só é aplicado do cadastrado, podendo não ter nenhum.
            </Text>
          <Flex direction="column" marginBottom="20px">
            <Text color="white" fontSize="24px">
              Critérios de Bônus
            </Text>
            {machineName && (
              <Text color="gray.400" fontSize="16px">
                Máquina: {machineName}.
              </Text>
            )}
          </Flex>

          {bonusCriteria.map((criteria, index) => (
            <BonusCriteriaCard
              key={criteria.id}
              title={`Critério de Bônus ${index + 1}`}
              minValue={criteria.minValue}
              bonusValue={criteria.bonusValue}
              onChangeMin={(e) => handleMinValueChange(criteria.id, e.target.value)}
              onChangeBonus={(e) => handleBonusValueChange(criteria.id, e.target.value)}
              onRemove={() => handleDeleteCriteria(criteria.id)}
            />
          ))}

          <Flex justifyContent="space-between" marginTop="20px">
            <Button
              leftIcon={<FaPlus />}
              colorScheme="blue"
              onClick={handleAddCriteria}
            >
              Adicionar Critério
            </Button>

            <Button
              colorScheme="green"
              isLoading={isLoading}
              loadingText="Salvando..."
              onClick={handleSaveBonusCriteria}
            >
              Salvar Critérios
            </Button>
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" onClick={onClose} color="white">
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BonusPaymentModule;